import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { LinkItem, useAppLayoutContextConsumer } from '@occc-app/design-system';

import { useRetailerNetworksForUser } from './use-retailer-networks-for-user.hook';
import { useRetailerConnectUser } from './use-retailer-connect-user.hook';

import { useGetRetailerNetworkQuery } from '../../data-access/queries';
import { routeCreator } from '../retailer-connect.utils';
import { RETAILER_CONNECT_TITLE } from '../retailer-connect.constants';
import { useRetailerNetworkUserSelector } from '../retailer-network-context/use-retailer-network-user.selector';

const NO_LINKS: LinkItem[] = [];

export const useBreadcrumbLinks = (links: LinkItem[] = NO_LINKS) => {
  const { networkId = '' } = useParams<{ networkId: string }>();

  const retailerNetworkHighestRoleUser = useRetailerConnectUser();
  const { isRetailerNetworkSiteUserRole, retailerNetworkUserUuid } =
    useRetailerNetworkUserSelector();

  const { data: retailerNetworksResult } = useRetailerNetworksForUser(2);

  const { data } = useGetRetailerNetworkQuery(
    {
      variables: { uuid: networkId },
    },
    !networkId,
  );

  const hasOnlyOneNetwork = retailerNetworksResult
    ? retailerNetworksResult.retailerNetworksForUser?.edges?.length === 1
    : undefined;
  const hasMultipleNetworks = hasOnlyOneNetwork === false;
  const canReturnToRetailerConnectRoot =
    retailerNetworkHighestRoleUser.isRetailerNetworkUserRole && hasMultipleNetworks;
  const retailerNetworkName = data?.retailerNetwork?.name;

  const breadcrumbLinks = useMemo(() => {
    const rootLink =
      isRetailerNetworkSiteUserRole &&
      retailerNetworkUserUuid &&
      networkId &&
      !canReturnToRetailerConnectRoot
        ? {
            to: routeCreator.userDetails(networkId, retailerNetworkUserUuid),
            label: RETAILER_CONNECT_TITLE,
          }
        : {
            to:
              networkId && hasOnlyOneNetwork
                ? routeCreator.overview(networkId)
                : routeCreator.base(),
            label: RETAILER_CONNECT_TITLE,
          };

    const networkLink =
      retailerNetworkName && hasOnlyOneNetwork === false
        ? [
            {
              label: retailerNetworkName,
              to: routeCreator.overview(networkId),
            },
          ]
        : [];

    return [rootLink, ...networkLink, ...links];
  }, [
    canReturnToRetailerConnectRoot,
    isRetailerNetworkSiteUserRole,
    retailerNetworkUserUuid,
    networkId,
    hasOnlyOneNetwork,
    retailerNetworkName,
    links,
  ]);

  useAppLayoutContextConsumer(breadcrumbLinks);
};
